import { faFilePen, faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Autocomplete, Box, Button, Chip, Dialog, Grid, TextField, Toolbar, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useStyles } from '../../hooks/useStyles';
import { api } from '../../api';
import { SimpleAlertProps } from '../../components/SimpleAlert';
import { useAuth } from '../../units/auth/AuthProvider';
import moment from 'moment';
import { washMicroSeconds } from '../../data/WashTime';
import { regLicensePlateNumber } from '../../data/LicensePlateNumber';
import { CarQueue } from '../../api/car-queue';

interface TimeOption {
    time: string;
    startMinutes: number;
    endMinutes: number;
}

const intervalMinutes = 7;

const buildTimeOptions = (): TimeOption[] => {
    const startMinutes = 9 * 60 + 30;
    const endMinutes = 20 * 60 + 30;

    const options: TimeOption[] = [];

    for (let minutes = startMinutes; minutes <= endMinutes; minutes += intervalMinutes) {
        const hours = Math.floor(minutes / 60)
            .toString()
            .padStart(2, '0');
        const mins = (minutes % 60).toString().padStart(2, '0');
        options.push({ time: `${hours}:${mins}`, startMinutes: minutes, endMinutes: minutes + intervalMinutes });
    }

    if (options[options.length - 1]?.time !== '20:30') {
        options.push({ time: '20:30', startMinutes: endMinutes, endMinutes: endMinutes + intervalMinutes });
    }

    return options;
};

const timeOptions = buildTimeOptions();

const buildEstimateTime = (date: string, time: string): Date => new Date(`${date}T${time}:00`);

export const useEditCarQueueDialog = (props: { simpleAlert?: SimpleAlertProps; reloadCarQueues?: () => void; carQueues?: CarQueue[] }) => {
    const { simpleAlert, reloadCarQueues, carQueues = [] } = props;
    const classes = useStyles();
    const auth = useAuth();
    const [open, setOpen] = useState(false);
    const [inputCarQueueId, setInputCarQueueId] = useState<string | undefined>(undefined);
    const [storeId, setStoreId] = useState<string | undefined>(undefined);
    const [existCarIds, setExistCarIds] = useState<string[]>([]);
    const [inputCarIds, setInputCarIds] = useState<string[]>([]);
    const [inputMaxCarNumber, setInputMaxCarNumber] = useState<number>(9);
    const [tempCarId, setTempCarId] = useState('');
    const [inputEstimateTime, setInputEstimateTime] = useState<Date>(new Date());
    const [date, setDate] = useState<string>(moment().format('YYYY-MM-DD'));
    const [time, setTime] = useState<string | undefined>(undefined);
    const [availableTimeOptions, setAvailableTimeOptions] = useState<TimeOption[]>(timeOptions);
    const [editMode, setEditMode] = useState<'line' | 'jump' | 'update'>('line');
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    const onCreateCars = async () => {
        await api.carQueue
            .createBulk(
                inputCarIds.map((carId, index, array) => ({
                    carId: carId,
                    estimateTime:
                        editMode == 'line'
                            ? new Date(new Date(inputEstimateTime!).getTime() + index * washMicroSeconds)
                            : editMode == 'jump'
                            ? new Date(new Date(inputEstimateTime!).getTime() - index * washMicroSeconds)
                            : new Date(new Date(inputEstimateTime!).getTime() + index * washMicroSeconds),
                    store: { storeId: storeId },
                })),
            )
            .then((ret) => {
                if (ret.success) {
                    reloadCarQueues && reloadCarQueues();
                    setOpen(false);
                    simpleAlert?.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                } else {
                    simpleAlert?.show({ autoHideDuration: 6000, severity: 'error', message: JSON.stringify(ret) });
                }
            });
    };
    const onUpdateCars = async () => {
        inputCarQueueId != undefined &&
            (await api.carQueue
                .updateBulk([
                    {
                        carQueueId: inputCarQueueId,
                        carId: inputCarIds[0],
                        estimateTime: inputEstimateTime,
                        store: { storeId: storeId },
                    },
                ])
                .then((ret) => {
                    if (ret.success) {
                        reloadCarQueues && reloadCarQueues();
                        setOpen(false);
                        simpleAlert?.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                    } else {
                        simpleAlert?.show({ autoHideDuration: 6000, severity: 'error', message: JSON.stringify(ret) });
                    }
                }));
    };
    const onReset = () => {
        setInputCarQueueId(undefined);
        setStoreId(undefined);
        setExistCarIds([]);
        setInputCarIds([]);
        setInputMaxCarNumber(9);
        setTempCarId('');
        setInputEstimateTime(new Date());
        setDate(moment().format('YYYY-MM-DD'));
        setTime(undefined);
        setAvailableTimeOptions(timeOptions);
    };
    useEffect(() => {
        if (open) {
            api.car
                .get({})
                .then((ret) => ret.data && setExistCarIds(ret.data.filter((d) => d.carId && d.carId.length > 0).map((d) => d.carId!)));
        } else {
            onReset();
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            return;
        }

        if (!date) {
            setAvailableTimeOptions(timeOptions);
            return;
        }

        const isToday = moment(date).isSame(moment(), 'day');
        const currentMinutes = moment().hours() * 60 + moment().minutes();

        // Get used times from carQueues on the selected date
        const usedTimes = new Set<string>();
        carQueues.forEach((carQueue) => {
            if (carQueue.estimateTime) {
                const estimateDate = moment(carQueue.estimateTime).format('YYYY-MM-DD');
                if (estimateDate === date) {
                    const timeStr = moment(carQueue.estimateTime).format('HH:mm');
                    usedTimes.add(timeStr);
                }
            }
        });

        const filteredTimeOptions = timeOptions.filter((option) => {
            const isPastTime = isToday && option.startMinutes < currentMinutes;
            const isUsedTime = usedTimes.has(option.time);
            return !isPastTime && !isUsedTime;
        });

        setAvailableTimeOptions(filteredTimeOptions);

        if (time) {
            const hasSelectedTime = filteredTimeOptions.some((option) => option.time === time);
            if (!hasSelectedTime) {
                setTime(undefined);
                setInputEstimateTime(new Date());
            }
        }
    }, [open, date, time, carQueues]);
    return {
        classes,
        auth,
        alert,
        open,
        setOpen,
        inputCarQueueId,
        setInputCarQueueId,
        storeId,
        setStoreId,
        existCarIds,
        setExistCarIds,
        inputCarIds,
        setInputCarIds,
        inputMaxCarNumber,
        setInputMaxCarNumber,
        tempCarId,
        setTempCarId,
        inputEstimateTime,
        setInputEstimateTime,
        editMode,
        setEditMode,
        onOpen,
        onClose,
        onCreateCars,
        onUpdateCars,
        onReset,
        date,
        setDate,
        time,
        setTime,
        availableTimeOptions,
        setAvailableTimeOptions,
    };
};

export interface EditCarQueueDialogProps extends ReturnType<typeof useEditCarQueueDialog> {}

export const EditCarQueueDialog = (props: EditCarQueueDialogProps): React.ReactElement => {
    return (
        <Dialog fullWidth={true} maxWidth={'lg'} open={props.open} onClose={props.onClose}>
            <AppBar position='sticky' className={props.classes.appBar} color='primary'>
                <Toolbar>
                    <Typography style={{ width: '100%', textAlign: 'center' }} variant='h4' component={'div'}>
                        {props.editMode == 'line' || props.editMode == 'jump'
                            ? '新增車輛'
                            : props.editMode == 'update'
                            ? '更新車輛'
                            : 'unknown edit mode'}
                    </Typography>
                    <Typography style={{ position: 'absolute', right: '0px' }} variant='h4' component={'div'}>
                        <Button color='inherit' onClick={props.onClose}>
                            <Typography variant='h5'>
                                <FontAwesomeIcon icon={faXmark} size='lg' fixedWidth />
                            </Typography>
                        </Button>
                    </Typography>
                </Toolbar>
            </AppBar>
            <Grid container flexDirection={'column'} padding={4}>
                <Grid container item flexDirection={'row'}>
                    <Grid container item flexDirection={'row'} xs={12}>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <Autocomplete
                                disabled={props.editMode == 'update'}
                                freeSolo
                                value={props.inputCarIds[0] || ''}
                                onChange={(event, newValue) => {
                                    if (typeof newValue === 'string') {
                                        const carId = newValue.toUpperCase();
                                        if (carId && regLicensePlateNumber.test(carId)) {
                                            props.setInputCarIds([carId]);
                                        } else if (!carId) {
                                            props.setInputCarIds([]);
                                        }
                                    }
                                }}
                                onInputChange={(event, newInputValue) => props.setTempCarId(newInputValue.toUpperCase())}
                                options={props.existCarIds}
                                getOptionLabel={(option) => option || ''}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={
                                            <>
                                                <span style={{ color: 'red' }}>*</span>
                                                {props.tempCarId.length > 0 && !regLicensePlateNumber.test(props.tempCarId)
                                                    ? '請輸入正確格式的車牌號碼'
                                                    : '請輸入車牌號碼'}
                                            </>
                                        }
                                        error={props.tempCarId.length > 0 && !regLicensePlateNumber.test(props.tempCarId)}
                                        placeholder='請輸入車牌號碼'
                                    />
                                )}
                                disableClearable={true}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <Autocomplete
                                options={props.availableTimeOptions}
                                groupBy={(option) => option.time.split(':')[0] + ':00'}
                                value={props.availableTimeOptions.find((option) => option.time === props.time) || null}
                                getOptionLabel={(option) => option.time}
                                onChange={(event, newValue) => {
                                    if (!newValue) {
                                        props.setTime(undefined);
                                        props.setInputEstimateTime(new Date());
                                        return;
                                    }

                                    props.setTime(newValue.time);
                                    if (props.date) {
                                        props.setInputEstimateTime(buildEstimateTime(props.date, newValue.time));
                                    }
                                }}
                                renderInput={(params) => <TextField {...params} label='預約時間' />}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            {props.editMode == 'line' || props.editMode == 'jump' ? (
                                <Button
                                    disabled={![props.inputCarIds.length > 0].every((v) => v)}
                                    className={props.classes.buttonCyan}
                                    style={{ margin: 4, marginTop: 12 }}
                                    onClick={props.onCreateCars}
                                >
                                    <FontAwesomeIcon icon={faPlus} fixedWidth />
                                    {'確定新增'}
                                </Button>
                            ) : props.editMode == 'update' ? (
                                <Button
                                    disabled={![props.inputCarIds.length > 0].every((v) => v)}
                                    className={props.classes.buttonCyan}
                                    style={{ margin: 4, marginTop: 12 }}
                                    onClick={props.onUpdateCars}
                                >
                                    <FontAwesomeIcon icon={faFilePen} fixedWidth />
                                    {'確定更新'}
                                </Button>
                            ) : (
                                <>{'unknown edit mode'}</>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Dialog>
    );
};
